import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTrainerSessions, acceptSession, declineSession } from '../lib/api';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Loader2, Inbox, MapPin, Clock, CalendarDays, CheckCircle2, X, Zap } from 'lucide-react';
import { CATEGORY_NAMES, formatCurrency, getInitials } from '../lib/utils';
import { toast } from 'sonner';

const Countdown = ({ to }) => {
  const [left, setLeft] = useState(() => Math.max(0, Math.floor((new Date(to).getTime() - Date.now()) / 1000)));
  useEffect(() => {
    if (!to) return;
    const id = setInterval(() => {
      setLeft(Math.max(0, Math.floor((new Date(to).getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [to]);
  if (!to) return null;
  const m = Math.floor(left / 60);
  const s = left % 60;
  return (
    <span className={`text-xs font-bold tabular-nums ${left < 60 ? 'text-red-300' : 'text-amber-300'}`} data-testid="countdown">
      {m}:{s.toString().padStart(2, '0')}
    </span>
  );
};

const RequestCard = ({ s, onAccept, onDecline, busy }) => {
  const client = s.client || {};
  const isInstant = s.booking_type === 'instant';
  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4"
      data-testid={`incoming-request-${s.id}`}
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-10 h-10 border border-zinc-700">
            <AvatarImage src={client.client_profile?.profile_image} />
            <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xs font-semibold">
              {getInitials(client.full_name || 'C')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{client.full_name || 'Client'}</p>
            <p className="text-xs text-zinc-400">{CATEGORY_NAMES[s.category] || s.category}</p>
          </div>
        </div>
        <div className="text-right">
          <Badge className={isInstant ? 'bg-red-500/15 text-red-300 border-red-500/30 mb-1' : 'bg-zinc-700 text-zinc-200 mb-1'}>
            {isInstant ? <><Zap className="w-3 h-3 mr-1 inline" /> Instant</> : 'Scheduled'}
          </Badge>
          {isInstant && s.auto_decline_at && (
            <div className="text-[10px] text-zinc-400">expires in <Countdown to={s.auto_decline_at} /></div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-zinc-300 mb-3">
        <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#C0A062]" />{s.scheduled_date}</div>
        <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C0A062]" />{s.scheduled_time}</div>
        <div className="flex items-center gap-1.5 col-span-2"><MapPin className="w-3.5 h-3.5 text-[#C0A062] shrink-0" /><span className="truncate">{s.location_address}</span></div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-zinc-400">You earn <span className="text-[#C0A062] font-bold">{formatCurrency(s.trainer_earnings)}</span></span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy === s.id}
            onClick={() => onDecline(s.id)}
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid={`decline-request-${s.id}`}
          >
            <X className="w-4 h-4 mr-1" /> Decline
          </Button>
          <Button
            size="sm"
            disabled={busy === s.id}
            onClick={() => onAccept(s.id)}
            className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
            data-testid={`accept-request-${s.id}`}
          >
            {busy === s.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};

const TrainerIncomingRequests = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrainerSessions();
      setSessions(res.data || []);
    } catch {
      // silent, dashboard already shows toast on auth errors elsewhere
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000); // poll every 8s for new requests
    return () => clearInterval(id);
  }, [load]);

  const pending = useMemo(
    () => sessions.filter((s) => s.status === 'pending_trainer_response'),
    [sessions]
  );

  const handleAccept = async (id) => {
    setBusy(id);
    try {
      await acceptSession(id);
      toast.success('Session confirmed');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not accept';
      toast.error(typeof msg === 'string' ? msg : 'Could not accept');
    } finally {
      setBusy(null);
    }
  };

  const handleDecline = async (id) => {
    setBusy(id);
    try {
      await declineSession(id);
      toast.message('Request declined');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not decline';
      toast.error(typeof msg === 'string' ? msg : 'Could not decline');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mb-8" data-testid="incoming-requests-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Inbox className="w-5 h-5 text-amber-400" />
          Incoming requests
          {pending.length > 0 && (
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">{pending.length}</Badge>
          )}
        </h2>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
      </div>
      {pending.length === 0 ? (
        <div
          className="text-zinc-500 text-sm py-8 text-center bg-[#18181B] border border-zinc-800 rounded-lg"
          data-testid="incoming-requests-empty"
        >
          {loading ? 'Loading\u2026' : 'No pending requests right now.'}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {pending.map((s) => (
            <RequestCard key={s.id} s={s} onAccept={handleAccept} onDecline={handleDecline} busy={busy} />
          ))}
        </div>
      )}
    </section>
  );
};

export default TrainerIncomingRequests;
